import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../axiosInterceptor';

export type ChatMessage = {
  id: string;
  sender_id: string;
  receiver_id: string;
  message: string;
  is_read?: boolean;
  sender_role?: string;
  sender_name?: string;
  sender_avatar?: string;
  timestamp?: string;
  is_delivered?: boolean;
  attachment?: any;
  type?: string;
};

export type ChatHistoryResponse = {
  messages: ChatMessage[];
  partner: {
    id: string;
    name?: string;
    email?: string;
    phone?: string;
    role?: string;
    location?: string;
    timezone?: string;
  };
  self: {
    id: string;
    name?: string;
    avatar?: string;
    email?: string;
    phone?: string;
    professional_bio?: string;
    role?: string;
    location?: string;
    timezone?: string;
  };
};

type SendChatPayload = {
  receiver_id: string;
  content: string;
  client_id?: string;
};

type ChatState = {
  sending: boolean;
  loadingHistory: boolean;
  error: boolean;
  errorMessage: string;
  messages: ChatMessage[];
  sendResult: unknown | null;
  pendingIds: string[];
  histories: Record<string, ChatMessage[]>;
};

const initialState: ChatState = {
  sending: false,
  loadingHistory: false,
  error: false,
  errorMessage: '',
  messages: [],
  sendResult: null,
  pendingIds: [],
  histories: {},
};

const normalizeMessages = (payload: any): ChatMessage[] => {
  const data = payload?.response ?? payload?.data ?? payload ?? {};
  if (data.messages && Array.isArray(data.messages)) {
    return data.messages as ChatMessage[];
  }
  return Array.isArray(data) ? (data as ChatMessage[]) : [];
};

const normalizeMessage = (payload: any): ChatMessage | null => {
  const data = payload?.response ?? payload?.data ?? payload ?? null;
  if (!data || typeof data !== 'object') {
    return null;
  }
  if (!('message' in data) && !('content' in data)) {
    return null;
  }
  if (!('receiver_id' in data)) {
    return null;
  }
  // Convert content to message for consistency
  if ('content' in data && !('message' in data)) {
    return { ...data, message: data.content } as ChatMessage;
  }
  return data as ChatMessage;
};

export const sendChatMessage = createAsyncThunk<unknown, SendChatPayload>(
  'chat/sendMessage',
  async (payload) => {
    const { receiver_id, content } = payload;
    const { data } = await api.post('/api/chats', { receiver_id, content });
    return data;
  }
);

export const getChatHistory = createAsyncThunk<
  ChatMessage[],
  string,
  { rejectValue: { status?: number; message?: string; data?: any } }
>('chat/getHistory', async (userId, thunkAPI) => {
  try {
    const { data } = await api.get(`/api/coach/chats/${userId}`);
    return normalizeMessages(data);
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      status: error?.response?.status || error?.status,
      message: error?.response?.data?.message || error?.message || 'Failed to load chat history.',
      data: error?.response?.data,
    });
  }
});

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    resetChat: (state) => {
      state.sending = false;
      state.loadingHistory = false;
      state.error = false;
      state.errorMessage = '';
      state.messages = [];
      state.sendResult = null;
      state.pendingIds = [];
      state.histories = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(sendChatMessage.pending, (state, action) => {
      state.sending = true;
      state.error = false;
      state.errorMessage = '';
      if (action.meta.arg.client_id) {
        const receiverId = action.meta.arg.receiver_id;
        state.pendingIds.push(action.meta.arg.client_id);
        const optimisticMessage: ChatMessage = {
          id: action.meta.arg.client_id,
          sender_id: '',
          receiver_id: action.meta.arg.receiver_id,
          message: action.meta.arg.content,
          is_read: true,
          sender_role: 'coach',
          timestamp: new Date().toISOString(),
        };
        state.messages.push(optimisticMessage);
        const history = state.histories[receiverId] ?? [];
        state.histories[receiverId] = [...history, optimisticMessage];
      }
    });
    builder.addCase(sendChatMessage.fulfilled, (state, action) => {
      state.sending = false;
      state.sendResult = action.payload;
      const clientId = action.meta.arg.client_id;
      if (clientId) {
        const receiverId = action.meta.arg.receiver_id;
        state.pendingIds = state.pendingIds.filter((id) => id !== clientId);
        const normalized = normalizeMessage(action.payload);
        if (normalized) {
          const index = state.messages.findIndex((item) => item.id === clientId);
          if (index >= 0) {
            state.messages[index] = normalized;
          } else {
            state.messages.push(normalized);
          }
          const history = state.histories[receiverId] ?? [];
          const historyIndex = history.findIndex((item) => item.id === clientId);
          if (historyIndex >= 0) {
            const updated = [...history];
            updated[historyIndex] = normalized;
            state.histories[receiverId] = updated;
          } else {
            state.histories[receiverId] = [...history, normalized];
          }
        }
      }
    });
    builder.addCase(sendChatMessage.rejected, (state, action) => {
      state.sending = false;
      state.error = true;
      state.errorMessage = action.error.message ?? 'Failed to send message.';
      const clientId = action.meta.arg.client_id;
      if (clientId) {
        const receiverId = action.meta.arg.receiver_id;
        state.pendingIds = state.pendingIds.filter((id) => id !== clientId);
        state.messages = state.messages.filter((item) => item.id !== clientId);
        const history = state.histories[receiverId] ?? [];
        state.histories[receiverId] = history.filter(
          (item) => item.id !== clientId
        );
      }
    });

    builder.addCase(getChatHistory.pending, (state) => {
      state.loadingHistory = true;
      state.error = false;
      state.errorMessage = '';
    });
    builder.addCase(getChatHistory.fulfilled, (state, action) => {
      state.loadingHistory = false;
      state.messages = action.payload;
      state.histories[action.meta.arg] = action.payload;
    });
    builder.addCase(getChatHistory.rejected, (state, action) => {
      state.loadingHistory = false;
      state.error = true;
      state.errorMessage =
        action.payload?.message || action.error.message || 'Failed to load chat history.';
      state.messages = [];
    });
  },
});

export const { resetChat } = chatSlice.actions;
export default chatSlice.reducer;
